import { Controller, useFormContext } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';

interface BaseOption {
  value: string;
  label: string;
}

interface Props<
  T extends BaseOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
}

export default function RHFAutocomplete<
  T extends BaseOption,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  label,
  placeholder,
  helperText,
  required,
  variant = 'outlined',
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control, setValue } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Autocomplete
          {...field}
          onChange={(event, newValue) => setValue(name, newValue, { shouldValidate: true })}
          isOptionEqualToValue={(option, val) => option.value === val?.value}
          renderInput={(params) => (
            <TextField
              label={label}
              required={required}
              placeholder={placeholder}
              error={!!error}
              helperText={error ? error?.message : helperText}
              variant={variant}
              {...params}
            />
          )}
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option.value}>
              <span>{option.label}</span>
            </li>
          )}
          {...other}
        />
      )}
    />
  );
}
